import { getBudgetFringeTotals } from "./BudgetItemUtils";
import { BudgetCategory, BudgetFringe, BudgetItem, BudgetSubCategory } from "./types";

export interface FringeRow {
  fringe: BudgetFringe;
  lineIds: number[];
  /** Account numbers of the lines it applies to, sorted. */
  accounts: string[];
  /** Edits and deletes are blocked while any line it applies to is locked. */
  hasLockedLines: boolean;
  /** Accounts holding those locked lines, for the message. */
  lockedAccounts: string[];
  estimate: number;
  actual: number;
}

/** Why a line cannot gain a fringe. Mirrors the backend rule in assert-fringe-assignment.ts. */
export type FringeLineBlock = "parent" | "locked" | "noAccountNumber";

export const fringeLineBlockText: Record<FringeLineBlock, string> = {
  parent: "Fringes go on the detail lines inside this line.",
  locked: "This line is pinned for Rebudget and can't change.",
  noAccountNumber: "Give this line an account number to add a fringe. AI Rebudget uses it.",
};

export interface FringeLine {
  item: BudgetItem;
  subCategory: BudgetSubCategory;
  categoryName: string;
  block: FringeLineBlock | null;
}

const withoutItems = ({ items: _items, ...fringe }: BudgetFringe): BudgetFringe => fringe;

const isLineLocked = (item: BudgetItem, subCategory?: Pick<BudgetSubCategory, "locked">) =>
  Boolean(item.locked || subCategory?.locked);

/** Without the account, relies on account locks being copied onto its lines. */
export function getFringeLineBlock(
  item: BudgetItem,
  subCategory?: Pick<BudgetSubCategory, "locked">
): FringeLineBlock | null {
  if (item.children?.length) return "parent";
  if (isLineLocked(item, subCategory)) return "locked";
  if (!item.account_number?.trim()) return "noAccountNumber";
  return null;
}

const forEachLine = (
  categories: BudgetCategory[],
  visit: (item: BudgetItem, subCategory: BudgetSubCategory, category: BudgetCategory) => void
) => {
  for (const category of categories) {
    for (const subCategory of category.sub_categories) {
      for (const item of subCategory.items) {
        visit(item, subCategory, category);
        for (const child of item.children ?? []) visit(child, subCategory, category);
      }
    }
  }
};

/** Leaf lines in budget order, each with the reason it can't gain a fringe, if any. */
export function collectFringeLines(categories: BudgetCategory[]): FringeLine[] {
  const lines: FringeLine[] = [];
  forEachLine(categories, (item, subCategory, category) => {
    if (item.children?.length) return;
    lines.push({
      item,
      subCategory,
      categoryName: category.name,
      block: getFringeLineBlock(item, subCategory),
    });
  });
  return lines;
}

/**
 * One row per fringe across the whole budget, including fringes with no lines. Amounts use
 * the budget-wide engine, so aggregate ceilings match the top sheet.
 */
export function buildFringeRows(
  definitions: BudgetFringe[] | undefined,
  categories: BudgetCategory[]
): FringeRow[] {
  const totals = new Map(
    getBudgetFringeTotals(categories).fringes.map((detail) => [
      detail.fringe.budget_fringe_id,
      detail,
    ])
  );
  const rows = new Map<number, FringeRow>();
  const rowFor = (fringe: BudgetFringe): FringeRow => {
    const existing = rows.get(fringe.budget_fringe_id);
    if (existing) return existing;
    const total = totals.get(fringe.budget_fringe_id);
    const row: FringeRow = {
      fringe: withoutItems(fringe),
      lineIds: [],
      accounts: [],
      hasLockedLines: false,
      lockedAccounts: [],
      estimate: total?.estimate ?? 0,
      actual: total?.actual ?? 0,
    };
    rows.set(fringe.budget_fringe_id, row);
    return row;
  };

  (definitions ?? []).forEach(rowFor);
  forEachLine(categories, (item, subCategory) => {
    for (const fringe of item.fringes ?? []) {
      const row = rowFor(fringe);
      row.lineIds.push(item.budget_item_id);
      const account = subCategory.account_number;
      if (account && !row.accounts.includes(account)) row.accounts.push(account);
      if (isLineLocked(item, subCategory)) {
        row.hasLockedLines = true;
        if (account && !row.lockedAccounts.includes(account)) row.lockedAccounts.push(account);
      }
    }
  });

  return [...rows.values()].map((row) => ({
    ...row,
    accounts: [...row.accounts].sort(),
    lockedAccounts: [...row.lockedAccounts].sort(),
  }));
}
